import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Check, ChevronLeft, ChevronRight, Copy, Droplet, Plus, Syringe, Trash2, TriangleAlert, X } from 'lucide-react';
import {
  APPOINTMENT_KIND_LABELS,
  BIRTH_CENTER_TERM_WINDOW,
  CHECKPOINT_CATEGORY_LABELS,
  CHECKPOINT_STATUS_LABELS,
  DATING_METHOD_LABELS,
  PREGNANCY_DECISIONS,
  PREGNANCY_DECISION_CATEGORIES,
  PREGNANCY_PLAN_DISCLAIMER,
  PREGNANCY_PROFILE_FIELDS,
  PREGNANCY_PROFILE_GROUPS,
  checkpointViews,
  dateAtGestationalAge,
  decisionApplies,
  formatCheckpointWindow,
  formatGestationalAge,
  formatShortDate,
  gestationalAgeOn,
  newPregnancyId,
  planBloodDraws,
  pregnancyAlerts,
  pregnancyDayInfo,
  pregnancyStageOn,
  pregnancyVisitBrief,
  resolvePregnancyDating,
  weightGainTarget,
} from '@homebase/shared/pregnancy';

const TABS = [
  ['today', 'Today'],
  ['calendar', 'Calendar'],
  ['plan', 'Plan'],
  ['decisions', 'Decisions'],
  ['visits', 'Visits'],
  ['info', 'Info'],
];
const TAB_KEY = 'homebase.pregnancyTab';
const TRIMESTER_LABELS = { 1: 'First trimester', 2: 'Second trimester', 3: 'Third trimester' };
const CAL_WEEKDAYS = ['S', 'M', 'T', 'W', 'T', 'F', 'S'];
const STATUS_GROUPS = [
  { id: 'attention', label: 'Needs booking', statuses: ['missed', 'due', 'soon'] },
  { id: 'booked', label: 'Booked', statuses: ['scheduled'] },
  { id: 'later', label: 'Later', statuses: ['upcoming'] },
  { id: 'closed', label: 'Done or skipped', statuses: ['done', 'declined', 'na'] },
];

function longDate(iso) {
  const [y, m, d] = iso.split('-').map(Number);
  return new Date(Date.UTC(y, m - 1, d)).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC' });
}

function SectionHead({ label, value, live }) {
  return <div className="eyebrowRow"><span className="eyebrow">{label}</span>{value ? <span className={live ? 'count live' : 'count'}>{value}</span> : null}</div>;
}

/** Text input that saves on blur / Enter instead of every keystroke. */
function CommitField({ value, onCommit, multiline, ...rest }) {
  const [draft, setDraft] = useState(value ?? '');
  useEffect(() => setDraft(value ?? ''), [value]);
  const commit = () => { if (String(draft) !== String(value ?? '')) onCommit(draft); };
  if (multiline) return <textarea className="field" rows={3} value={draft} onChange={(event) => setDraft(event.target.value)} onBlur={commit} {...rest} />;
  return <input className="field" value={draft} onChange={(event) => setDraft(event.target.value)} onBlur={commit} onKeyDown={(event) => { if (event.key === 'Enter') event.currentTarget.blur(); }} {...rest} />;
}

function StickIcon({ stick }) {
  if (stick === 'venous') return <span className="stick venous" title="Blood draw"><Syringe size={14} strokeWidth={2.2} />Blood draw</span>;
  if (stick === 'fingerstick') return <span className="stick" title="Fingerstick"><Droplet size={14} strokeWidth={2.2} />Fingerstick</span>;
  return null;
}

function stageLine(stage) {
  if (stage.kind !== 'pregnant') return '';
  return `Week ${stage.weekNumber} · Month ${stage.month} · ${TRIMESTER_LABELS[stage.trimester]}`;
}

export function pregnancyLauncherStatus(data, today) {
  if (!data) return 'Set up';
  const stage = pregnancyStageOn(data, today);
  if (stage.kind === 'pregnant') return `${formatGestationalAge(stage.ga)} · week ${stage.weekNumber}`;
  if (stage.kind === 'postpartum') return `${stage.daysSinceBirth} days postpartum`;
  return 'Add last period date';
}

export function PregnancyApp({ data, today, onMutate, unavailable }) {
  const [tab, setTab] = useState(() => {
    try { return window.sessionStorage.getItem(TAB_KEY) || 'today'; } catch { return 'today'; }
  });
  const [focusId, setFocusId] = useState(null);
  const [apptDraft, setApptDraft] = useState(null);
  const [briefDate, setBriefDate] = useState(null);
  const dating = useMemo(() => resolvePregnancyDating(data.dating), [data.dating]);
  const views = useMemo(() => checkpointViews(data, today), [data, today]);
  const alerts = useMemo(() => pregnancyAlerts(data, today), [data, today]);

  useEffect(() => { try { window.sessionStorage.setItem(TAB_KEY, tab); } catch { /* ignore */ } }, [tab]);

  function go(nextTab, nextFocus = null) {
    setTab(nextTab);
    setFocusId(nextFocus);
    window.scrollTo({ top: 0 });
  }

  function book(view) {
    const kind = view.checkpoint.category === 'ultrasound' ? 'ultrasound' : view.checkpoint.category === 'lab' ? 'lab' : view.checkpoint.category === 'prep' ? 'other' : 'prenatal';
    // Booking any blood test books its whole draw, so everything shares one stick.
    const draw = view.checkpoint.bundle ? planBloodDraws(data, today).find((entry) => entry.checkpointIds.includes(view.checkpoint.id)) : null;
    const window = draw?.window ?? view.window;
    const suggested = today > window.startDate ? today : window.startDate;
    const checkpointIds = draw ? draw.checkpointIds.filter((id) => views.find((entry) => entry.checkpoint.id === id && !['done', 'scheduled'].includes(entry.status))) : [view.checkpoint.id];
    setApptDraft(blankAppointment({ date: suggested, kind, title: draw ? draw.label : view.checkpoint.title, checkpointIds }));
    go('visits');
  }

  if (unavailable) {
    return (
      <section>
        <div className="viewHead"><h1 className="pageTitle">Pregnancy</h1></div>
        <div className="noticeBlock errorBlock">The pregnancy service isn’t available on this API yet. Deploy the API, then reload.</div>
      </section>
    );
  }

  return (
    <section>
      <div className="viewHead">
        <div className="metaStrip"><span>{longDate(today)}</span>{dating ? <span>Due {formatShortDate(dating.edd)}</span> : null}</div>
        <h1 className="pageTitle">Pregnancy</h1>
      </div>
      <div className="pillTabs" role="tablist">
        {TABS.map(([id, label]) => (
          <button key={id} className={tab === id ? 'on' : ''} role="tab" aria-selected={tab === id} type="button" onClick={() => go(id)}>
            {label}
            {id === 'today' && alerts.some((alert) => alert.level === 'action') ? <span className="tabDot" aria-label="needs attention" /> : null}
          </button>
        ))}
      </div>
      {!dating && tab !== 'info' ? <DatingSetup data={data} onMutate={onMutate} /> : null}
      {dating && tab === 'today' ? <TodayTab data={data} today={today} edd={dating.edd} alerts={alerts} views={views} go={go} onBrief={setBriefDate} /> : null}
      {dating && tab === 'calendar' ? <CalendarTab data={data} today={today} edd={dating.edd} views={views} go={go} /> : null}
      {dating && tab === 'plan' ? <PlanTab data={data} today={today} views={views} focusId={focusId} onMutate={onMutate} onBook={book} /> : null}
      {dating && tab === 'decisions' ? <DecisionsTab data={data} today={today} edd={dating.edd} focusId={focusId} onMutate={onMutate} /> : null}
      {dating && tab === 'visits' ? <VisitsTab data={data} today={today} edd={dating.edd} views={views} draft={apptDraft} setDraft={setApptDraft} onMutate={onMutate} onBrief={setBriefDate} /> : null}
      {tab === 'info' ? <InfoTab data={data} onMutate={onMutate} /> : null}
      {briefDate ? <BriefDialog data={data} date={briefDate} onClose={() => setBriefDate(null)} /> : null}
    </section>
  );
}

// ---------- setup ----------

function DatingSetup({ data, onMutate }) {
  const [lmp, setLmp] = useState(data.dating.lmp || '');
  const [cycle, setCycle] = useState(String(data.dating.cycleLengthDays || 28));
  return (
    <div className="noteBlock accent">
      <div className="noteHead"><span className="eyebrow">Start here</span></div>
      <p className="noteBody">Enter the first day of her last period. Weeks, the due date, every test window, and reminders are calculated from it. You can add an ultrasound date later in Info and the dating will adjust.</p>
      <form className="formGrid flush" onSubmit={(event) => { event.preventDefault(); if (!lmp) return; onMutate((draft) => ({ ...draft, dating: { ...draft.dating, lmp, cycleLengthDays: Number(cycle) || 28 } })); }}>
        <div className="formPair">
          <label><span>Last period started</span><input className="field" type="date" value={lmp} onChange={(event) => setLmp(event.target.value)} required /></label>
          <label><span>Usual cycle (days)</span><input className="field" inputMode="numeric" value={cycle} onChange={(event) => setCycle(event.target.value)} /></label>
        </div>
        <button className="btnPrimary" type="submit" disabled={!lmp}>Save and calculate</button>
      </form>
    </div>
  );
}

// ---------- today ----------

function WeekRibbon({ totalDays, onPickWeek }) {
  const current = Math.floor(totalDays / 7);
  return (
    <div className="weekRibbon" aria-label={`Week ${current + 1} of 40`}>
      {Array.from({ length: 40 }, (_, week) => (
        <button
          key={week}
          className={`${week < current ? 'past' : week === current ? 'now' : ''}${week === 13 || week === 27 ? ' triEnd' : ''}`}
          type="button"
          title={`${week}w`}
          aria-label={`Show ${week} weeks on the calendar`}
          onClick={() => onPickWeek(week)}
        />
      ))}
    </div>
  );
}

/** Overdue decisions collapse into one row so they don't bury bookings. */
function groupDecisionAlerts(alerts) {
  const overdue = alerts.filter((alert) => alert.decisionId && alert.level === 'action');
  if (overdue.length < 2) return alerts;
  const merged = {
    id: 'decide-overdue',
    level: 'action',
    decisionId: overdue[0].decisionId,
    title: `${overdue.length} decisions past their target week`,
    detail: overdue.map((alert) => alert.title.replace(/^Decide: /, '')).join(', '),
  };
  const firstIndex = alerts.indexOf(overdue[0]);
  const rest = alerts.filter((alert) => !overdue.includes(alert));
  return [...rest.slice(0, firstIndex), merged, ...rest.slice(firstIndex)];
}

function TodayTab({ data, today, edd, alerts, views, go, onBrief }) {
  const stage = pregnancyStageOn(data, today);
  const next = data.appointments.find((appt) => appt.status === 'scheduled' && appt.date >= today) ?? null;
  const draws = planBloodDraws(data, today);
  const remainingDraws = draws.filter((draw) => draw.status !== 'done');
  const [showAll, setShowAll] = useState(false);
  const rows = groupDecisionAlerts(alerts);
  const visibleRows = showAll ? rows : rows.slice(0, 6);
  const openNow = views.filter((view) => view.status === 'due' || view.status === 'scheduled' && today >= view.window.startDate && today <= view.window.endDate);

  return (
    <>
      {stage.kind === 'pregnant' ? (
        <div className="gaHero">
          <div className="gaBig">{stage.ga.weeks}<small>w</small> {stage.ga.days}<small>d</small></div>
          <div className="gaMeta">{stageLine(stage)}</div>
          <WeekRibbon totalDays={stage.ga.totalDays} onPickWeek={(week) => go('calendar', dateAtGestationalAge(edd, week))} />
          <div className="gaFoot"><span>Due {longDate(edd)}</span><span>{stage.daysToDue >= 0 ? `${stage.daysToDue} days to go` : `${-stage.daysToDue} days past due`}</span></div>
          {stage.ga.totalDays >= BIRTH_CENTER_TERM_WINDOW.start && stage.ga.totalDays <= BIRTH_CENTER_TERM_WINDOW.end ? <div className="gaFlag">In the birth center term window</div> : null}
        </div>
      ) : (
        <div className="gaHero"><div className="gaMeta">{stage.kind === 'postpartum' ? `${stage.daysSinceBirth} days since birth` : 'Before the dating start date'}</div></div>
      )}

      <SectionHead label="Needs attention" value={rows.length ? String(rows.length) : ''} live={rows.some((alert) => alert.level === 'action')} />
      {rows.length ? (
        <div className="rowGroup">
          {visibleRows.map((alert) => (
            <button
              className="alertRow"
              key={alert.id}
              type="button"
              onClick={() => (alert.decisionId ? go('decisions', alert.decisionId) : alert.checkpointId ? go('plan', alert.checkpointId) : alert.id === 'cadence' ? go('visits') : go('info'))}
            >
              <span className={`alertDot ${alert.level}`} />
              <span className="alertText"><span className="alertTitle">{alert.title}</span><span className="rowMeta">{alert.detail}</span></span>
              <ChevronRight size={18} strokeWidth={2} className="alertChevron" />
            </button>
          ))}
          {rows.length > visibleRows.length ? <button className="alertRow moreRow" type="button" onClick={() => setShowAll(true)}>Show {rows.length - visibleRows.length} more</button> : null}
        </div>
      ) : <div className="emptyBlock">Nothing needs booking or deciding right now.</div>}

      <SectionHead label="Next appointment" />
      {next ? (
        <div className="noteBlock flushTop">
          <div className="noteHead"><span className="eyebrow">{longDate(next.date)}{next.time ? `, ${next.time}` : ''}</span><span className="noteValue">{formatGestationalAge(gestationalAgeOn(edd, next.date))}</span></div>
          <p className="noteBody">{next.title || APPOINTMENT_KIND_LABELS[next.kind]}{next.location ? ` at ${next.location}` : ''}</p>
          <div className="actionRow flush"><button className="btn live" type="button" onClick={() => onBrief(next.date)}>Visit brief</button><button className="btn" type="button" onClick={() => go('visits')}>All visits</button></div>
        </div>
      ) : <div className="emptyBlock">No visit booked. <button className="textBtn accent" type="button" onClick={() => go('visits')}>Add one</button></div>}

      {openNow.length ? (
        <>
          <SectionHead label="Open windows now" value={String(openNow.length)} />
          <div className="rowGroup">
            {openNow.map((view) => (
              <button className="leadRow rowMain" key={view.checkpoint.id} type="button" onClick={() => go('plan', view.checkpoint.id)}>
                <span className="lab">{view.checkpoint.title}</span><span className="lead" /><span className="val">until {formatShortDate(view.window.endDate)}</span>
              </button>
            ))}
          </div>
        </>
      ) : null}

      <button className="noteBlock drawSummary" type="button" onClick={() => go('plan')}>
        <div className="noteHead"><span className="eyebrow">Blood draws planned</span><span className="noteValue">{remainingDraws.length} left</span></div>
        <p className="noteBody">{remainingDraws.length ? remainingDraws.map((draw) => `${draw.label}${draw.window ? ` (${formatShortDate(draw.window.startDate)}–${formatShortDate(draw.window.endDate)})` : ''}`).join(', ') : 'All planned draws are done.'}</p>
      </button>
    </>
  );
}

// ---------- calendar ----------

function monthKey(iso) { return iso.slice(0, 7); }

function shiftMonth(key, delta) {
  const [y, m] = key.split('-').map(Number);
  const date = new Date(Date.UTC(y, m - 1 + delta, 1));
  return `${date.getUTCFullYear()}-${String(date.getUTCMonth() + 1).padStart(2, '0')}`;
}

function CalendarTab({ data, today, edd, views, go }) {
  const [selected, setSelected] = useState(today);
  const [month, setMonth] = useState(monthKey(today));
  const [weekInput, setWeekInput] = useState('');
  const [y, m] = month.split('-').map(Number);
  const firstWeekday = new Date(Date.UTC(y, m - 1, 1)).getUTCDay();
  const dayCount = new Date(Date.UTC(y, m, 0)).getUTCDate();
  const cells = [...Array.from({ length: firstWeekday }, () => null), ...Array.from({ length: dayCount }, (_, i) => `${month}-${String(i + 1).padStart(2, '0')}`)];
  const info = pregnancyDayInfo(data, selected, views);
  const monthLabel = new Date(Date.UTC(y, m - 1, 1)).toLocaleDateString('en-US', { month: 'long', year: 'numeric', timeZone: 'UTC' });

  function pick(date) { setSelected(date); setMonth(monthKey(date)); }

  // Swipe the grid sideways to change month (phones); vertical drags still scroll.
  const touchStart = useRef(null);
  function onTouchEnd(event) {
    const start = touchStart.current;
    touchStart.current = null;
    if (!start) return;
    const touch = event.changedTouches[0];
    const dx = touch.clientX - start.x;
    const dy = touch.clientY - start.y;
    if (Math.abs(dx) > 50 && Math.abs(dx) > Math.abs(dy) * 1.5) setMonth(shiftMonth(month, dx < 0 ? 1 : -1));
  }

  return (
    <>
      <div className="calHead">
        <button className="iconBtn" type="button" aria-label="Previous month" onClick={() => setMonth(shiftMonth(month, -1))}><ChevronLeft size={20} /></button>
        <span className="calMonth">{monthLabel}</span>
        <button className="iconBtn" type="button" aria-label="Next month" onClick={() => setMonth(shiftMonth(month, 1))}><ChevronRight size={20} /></button>
      </div>
      <div className="calGrid pregCal" onTouchStart={(event) => { const touch = event.touches[0]; touchStart.current = { x: touch.clientX, y: touch.clientY }; }} onTouchEnd={onTouchEnd}>
        {CAL_WEEKDAYS.map((label, index) => <span className="calWeekday" key={index}>{label}</span>)}
        {cells.map((date, index) => {
          if (!date) return <span key={index} />;
          const ga = gestationalAgeOn(edd, date).totalDays;
          const inPregnancy = ga >= 0 && ga <= 294;
          const dayInfo = inPregnancy || date === selected ? pregnancyDayInfo(data, date, views) : null;
          const classes = ['pregDay'];
          if (inPregnancy) classes.push(`tri${ga < 98 ? 1 : ga < 196 ? 2 : 3}`);
          if (date === today) classes.push('today');
          if (date === selected) classes.push('selected');
          if (date === edd) classes.push('due');
          if (dayInfo?.birthCenterWindow) classes.push('termWindow');
          return (
            <button className={classes.join(' ')} key={date} type="button" onClick={() => setSelected(date)} aria-label={`${date}${inPregnancy ? `, ${formatGestationalAge(ga)}` : ''}`}>
              <span className="pregDayNum">{Number(date.slice(8))}</span>
              {inPregnancy && dayInfo?.isWeekStart ? <span className="pregWeek">{Math.floor(ga / 7)}w</span> : null}
              {dayInfo && (dayInfo.appointments.length || dayInfo.opening.length) ? (
                <span className="pregDots">{dayInfo.appointments.length ? <i className="appt" /> : null}{dayInfo.opening.length ? <i /> : null}</span>
              ) : null}
            </button>
          );
        })}
      </div>
      <div className="calLegend">
        <span><i className="lg tri1" />1st</span><span><i className="lg tri2" />2nd</span><span><i className="lg tri3" />3rd</span><span><i className="lg dotAppt" />Visit</span><span><i className="lg dotOpen" />Window opens</span>
      </div>
      <form className="weekJump" onSubmit={(event) => { event.preventDefault(); const week = Number(weekInput); if (week >= 0 && week <= 42) pick(dateAtGestationalAge(edd, week)); }}>
        <input className="field" inputMode="numeric" placeholder="Jump to week, e.g. 20" value={weekInput} onChange={(event) => setWeekInput(event.target.value)} aria-label="Jump to gestational week" />
        <button className="btn" type="submit">Go</button>
        <button className="btn" type="button" onClick={() => pick(today)}>Today</button>
      </form>
      <DayDetail info={info} edd={edd} go={go} />
    </>
  );
}

function DayDetail({ info, edd, go }) {
  const { stage } = info;
  return (
    <div className="noteBlock dayDetail">
      <div className="eyebrow">{longDate(info.date)}</div>
      {stage.kind === 'pregnant' ? (
        <>
          <div className="dayGa">{formatGestationalAge(stage.ga)}</div>
          <div className="noteBody">{stageLine(stage)}</div>
          <div className="noteBody">{stage.daysToDue > 0 ? `${stage.daysToDue} days before the due date` : stage.daysToDue === 0 ? 'Due date' : `${-stage.daysToDue} days past the due date`}. Week {stage.weekNumber} runs {formatShortDate(dateAtGestationalAge(edd, stage.ga.weeks))}–{formatShortDate(dateAtGestationalAge(edd, stage.ga.weeks, 6))}.</div>
          {info.birthCenterWindow ? <div className="gaFlag">Birth center term window</div> : null}
        </>
      ) : <div className="noteBody">{stage.kind === 'postpartum' ? `${stage.daysSinceBirth} days postpartum` : 'Outside the pregnancy'}</div>}
      {info.appointments.length ? <DetailList title="Appointments" items={info.appointments.map((appt) => `${appt.time ? `${appt.time} ` : ''}${appt.title || APPOINTMENT_KIND_LABELS[appt.kind]}`)} onClick={() => go('visits')} /> : null}
      {info.opening.length ? <DetailList title="Windows opening" items={info.opening.map((cp) => cp.title)} onClick={() => go('plan', info.opening[0].id)} /> : null}
      {info.closing.length ? <DetailList title="Last day for" items={info.closing.map((cp) => cp.title)} onClick={() => go('plan', info.closing[0].id)} /> : null}
      {info.decisionsDue.length ? <DetailList title="Decide by today" items={info.decisionsDue.map((decision) => decision.title)} onClick={() => go('decisions', info.decisionsDue[0].id)} /> : null}
      {info.open.length ? <DetailList title="Open windows" items={info.open.map((view) => `${view.checkpoint.title} (${CHECKPOINT_STATUS_LABELS[view.status].toLowerCase()})`)} onClick={() => go('plan', info.open[0].checkpoint.id)} /> : null}
    </div>
  );
}

function DetailList({ title, items, onClick }) {
  return (
    <button className="detailList" type="button" onClick={onClick}>
      <span className="detailLabel">{title}</span>
      {items.map((item, index) => <span className="detailItem" key={index}>{item}</span>)}
    </button>
  );
}

// ---------- plan ----------

function PlanTab({ data, today, views, focusId, onMutate, onBook }) {
  const draws = planBloodDraws(data, today);
  const bundleDates = Object.fromEntries(views.filter((view) => view.checkpoint.bundle && view.status === 'scheduled' && view.appointment).map((view) => [view.checkpoint.bundle, view.appointment.date]));
  const [openId, setOpenId] = useState(focusId);
  const [showClosed, setShowClosed] = useState(false);
  useEffect(() => {
    if (!focusId) return;
    setOpenId(focusId);
    const view = views.find((entry) => entry.checkpoint.id === focusId);
    if (view && ['done', 'declined', 'na'].includes(view.status)) setShowClosed(true);
    window.setTimeout(() => document.getElementById(`cp-${focusId}`)?.scrollIntoView({ block: 'center', behavior: 'smooth' }), 50);
  }, [focusId]);

  function setOverride(id, override, extra = {}) {
    onMutate((draft) => {
      const current = draft.checkpoints[id] ?? { override: null, date: null, note: '' };
      return { ...draft, checkpoints: { ...draft.checkpoints, [id]: { ...current, override, ...extra } } };
    });
  }

  return (
    <>
      <SectionHead label="Blood draws" value={`${draws.length} planned`} live />
      <div className="rowGroup">
        {draws.map((draw) => (
          <div className="drawRow" key={draw.id}>
            <div className="drawTop">
              <span className="lab"><Syringe size={15} strokeWidth={2.2} className="inlineIcon" />{draw.label}</span>
              <span className={`statusChip ${draw.status}`}>{draw.status === 'done' ? 'Done' : draw.status === 'scheduled' ? `Booked ${draw.scheduledDates.map(formatShortDate).join(', ')}` : 'Not booked'}</span>
            </div>
            <span className="rowMeta">{draw.window ? `${formatShortDate(draw.window.startDate)} – ${formatShortDate(draw.window.endDate)}` : 'Windows don’t overlap; ask about splitting'}</span>
            <span className="drawTests">{draw.tests.join(' · ')}</span>
          </div>
        ))}
      </div>
      <p className="hintBlock">Ask for every test in a group on the same stick. Swabs, urine, and fingersticks aren’t counted.</p>

      {STATUS_GROUPS.map((group) => {
        const members = views.filter((view) => group.statuses.includes(view.status));
        if (!members.length) return null;
        const collapsed = group.id === 'closed' && !showClosed;
        return (
          <div key={group.id}>
            <SectionHead label={group.label} value={String(members.length)} live={group.id === 'attention'} />
            {collapsed ? <div className="pad"><button className="btnTiny" type="button" onClick={() => setShowClosed(true)}>Show {members.length}</button></div> : (
              <div className="rowGroup">
                {members.map((view) => (
                  <CheckpointRow key={view.checkpoint.id} view={view} bundleDate={view.appointment ? null : bundleDates[view.checkpoint.bundle]} open={openId === view.checkpoint.id} today={today} state={data.checkpoints[view.checkpoint.id]} onToggle={() => setOpenId(openId === view.checkpoint.id ? null : view.checkpoint.id)} onOverride={setOverride} onBook={onBook} />
                ))}
              </div>
            )}
          </div>
        );
      })}
      <p className="hintBlock">{PREGNANCY_PLAN_DISCLAIMER}</p>
    </>
  );
}

function CheckpointRow({ view, bundleDate, open, today, state, onToggle, onOverride, onBook }) {
  const { checkpoint, window: win, status } = view;
  return (
    <div className={`cpRow${open ? ' open' : ''}`} id={`cp-${checkpoint.id}`}>
      <button className="cpMain" type="button" onClick={onToggle} aria-expanded={open}>
        <span className="cpTop">
          <span className="lab">{checkpoint.title}</span>
          <span className={`statusChip ${status}`}>{CHECKPOINT_STATUS_LABELS[status]}</span>
        </span>
        <span className="rowMeta">{CHECKPOINT_CATEGORY_LABELS[checkpoint.category]}, {formatCheckpointWindow(checkpoint)}, {formatShortDate(win.startDate)} – {formatShortDate(win.endDate)}</span>
        <span className="cpTags">
          <StickIcon stick={checkpoint.stick} />
          {view.appointment ? <span className="tag">Booked {formatShortDate(view.appointment.date)}</span> : null}
          {bundleDate && !['done', 'declined', 'na'].includes(status) ? <span className="tag warn">Add to the {formatShortDate(bundleDate)} draw</span> : null}
          {view.outsideWindow ? <span className="tag warn"><TriangleAlert size={13} />Outside window</span> : null}
          {view.pendingDecision ? <span className="tag">Assumed until decided</span> : null}
        </span>
      </button>
      {open ? (
        <div className="cpBody">
          <p className="bodyText">{checkpoint.summary}</p>
          {checkpoint.tests?.length ? <p className="rowMeta">Includes: {checkpoint.tests.join(', ')}</p> : null}
          {checkpoint.tip ? <p className="hint live">{checkpoint.tip}</p> : null}
          <div className="actionRow flush">
            {status !== 'done' && status !== 'scheduled' ? <button className="btn live" type="button" onClick={() => onBook(view)}><Plus size={16} />Book</button> : null}
            <button className={state?.override === 'done' ? 'btn on' : 'btn'} type="button" onClick={() => onOverride(checkpoint.id, state?.override === 'done' ? null : 'done', { date: state?.date ?? today })}>Done</button>
            <button className={state?.override === 'declined' ? 'btn on' : 'btn'} type="button" onClick={() => onOverride(checkpoint.id, state?.override === 'declined' ? null : 'declined')}>Declined</button>
            <button className={state?.override === 'na' ? 'btn on' : 'btn'} type="button" onClick={() => onOverride(checkpoint.id, state?.override === 'na' ? null : 'na')}>Not needed</button>
          </div>
          <div className="formGrid flush">
            {state?.override === 'done' ? <label><span>Done on</span><input className="field" type="date" value={state?.date ?? ''} onChange={(event) => onOverride(checkpoint.id, 'done', { date: event.target.value || null })} /></label> : null}
            <label><span>Note</span><CommitField value={state?.note ?? ''} placeholder="Result, who we talked to, why we skipped" onCommit={(note) => onOverride(checkpoint.id, state?.override ?? null, { note })} /></label>
          </div>
        </div>
      ) : null}
    </div>
  );
}

// ---------- decisions ----------

function DecisionsTab({ data, today, edd, focusId, onMutate }) {
  const todayGa = gestationalAgeOn(edd, today).totalDays;
  const applicable = PREGNANCY_DECISIONS.filter((decision) => decisionApplies(data, decision));
  const undecided = applicable.filter((decision) => !data.decisions[decision.id]?.choice).length;

  useEffect(() => {
    if (focusId) window.setTimeout(() => document.getElementById(`dc-${focusId}`)?.scrollIntoView({ block: 'center', behavior: 'smooth' }), 50);
  }, [focusId]);

  function update(id, patch) {
    onMutate((draft) => {
      const current = draft.decisions[id] ?? { choice: null, note: '', decidedAt: null };
      return { ...draft, decisions: { ...draft.decisions, [id]: { ...current, ...patch } } };
    });
  }

  return (
    <>
      <div className="noteBlock accent">
        <div className="noteHead"><span className="eyebrow">Decide before the visit</span><span className="noteValue">{undecided} open</span></div>
        <p className="noteBody">Your answers show up in the visit brief so you can hand the midwife your plan. Options marked “fewer draws” keep needles to a minimum.</p>
      </div>
      {PREGNANCY_DECISION_CATEGORIES.map((category) => {
        const decisions = applicable.filter((decision) => decision.category === category.id).sort((a, b) => a.decideByWeek - b.decideByWeek);
        if (!decisions.length) return null;
        return (
          <div key={category.id}>
            <SectionHead label={category.label} value={`${decisions.filter((decision) => data.decisions[decision.id]?.choice).length} of ${decisions.length}`} />
            <div className="rowGroup">
              {decisions.map((decision) => {
                const state = data.decisions[decision.id];
                const overdue = !state?.choice && todayGa >= decision.decideByWeek * 7;
                return (
                  <div className={`decisionCard${focusId === decision.id ? ' focused' : ''}`} id={`dc-${decision.id}`} key={decision.id}>
                    <div className="cpTop"><span className="lab">{decision.title}</span><span className={`statusChip ${state?.choice ? 'done' : overdue ? 'missed' : 'upcoming'}`}>{state?.choice ? 'Decided' : `By ${decision.decideByWeek}w`}</span></div>
                    <span className="rowMeta">Decide by {decision.decideByWeek}w, {formatShortDate(dateAtGestationalAge(edd, decision.decideByWeek))}</span>
                    <p className="bodyText small">{decision.context}</p>
                    <div className="optionList">
                      {decision.options.map((option) => (
                        <button
                          key={option.id}
                          className={state?.choice === option.id ? 'optionBtn on' : 'optionBtn'}
                          type="button"
                          aria-pressed={state?.choice === option.id}
                          onClick={() => update(decision.id, state?.choice === option.id ? { choice: null, decidedAt: null } : { choice: option.id, decidedAt: new Date().toISOString() })}
                        >
                          <span className="optionCheck">{state?.choice === option.id ? <Check size={14} strokeWidth={3} /> : null}</span>
                          <span className="optionText"><span>{option.label}</span>{option.detail ? <span className="rowMeta">{option.detail}</span> : null}</span>
                          {option.fewerDraws ? <span className="tag good">Fewer draws</span> : null}
                        </button>
                      ))}
                    </div>
                    <CommitField value={state?.note ?? ''} placeholder="Note (optional)" onCommit={(note) => update(decision.id, { note })} />
                  </div>
                );
              })}
            </div>
          </div>
        );
      })}
      <p className="hintBlock">{PREGNANCY_PLAN_DISCLAIMER}</p>
    </>
  );
}

// ---------- visits ----------

function blankAppointment(patch = {}) {
  return { id: null, date: '', time: '', kind: 'prenatal', title: '', provider: '', location: '', notes: '', checkpointIds: [], status: 'scheduled', ...patch };
}

function VisitsTab({ data, today, edd, views, draft, setDraft, onMutate, onBrief }) {
  const upcoming = data.appointments.filter((appt) => appt.date >= today && appt.status !== 'cancelled');
  const past = data.appointments.filter((appt) => appt.date < today || appt.status === 'cancelled').reverse();

  function saveAppointment(appt) {
    onMutate((doc) => {
      const record = { ...appt, id: appt.id ?? newPregnancyId() };
      const exists = doc.appointments.some((entry) => entry.id === record.id);
      return { ...doc, appointments: exists ? doc.appointments.map((entry) => (entry.id === record.id ? record : entry)) : [...doc.appointments, record] };
    });
    setDraft(null);
  }

  return (
    <>
      {draft ? <AppointmentForm draft={draft} edd={edd} views={views} onCancel={() => setDraft(null)} onSave={saveAppointment} onDelete={draft.id ? () => { onMutate((doc) => ({ ...doc, appointments: doc.appointments.filter((entry) => entry.id !== draft.id) })); setDraft(null); } : null} /> : (
        <div className="actionRow"><button className="btn live" type="button" onClick={() => setDraft(blankAppointment({ date: today }))}><Plus size={16} />Add appointment</button></div>
      )}
      <SectionHead label="Upcoming" value={String(upcoming.length)} />
      {upcoming.length ? <div className="rowGroup">{upcoming.map((appt) => <AppointmentRow key={appt.id} appt={appt} edd={edd} onEdit={() => setDraft(appt)} onBrief={() => onBrief(appt.date)} />)}</div> : <div className="emptyBlock">Nothing booked ahead.</div>}
      {past.length ? (
        <>
          <SectionHead label="Past" value={String(past.length)} />
          <div className="rowGroup">{past.map((appt) => <AppointmentRow key={appt.id} appt={appt} edd={edd} onEdit={() => setDraft(appt)} />)}</div>
        </>
      ) : null}
      <QuestionsSection data={data} onMutate={onMutate} />
      <MeasurementsSection data={data} today={today} onMutate={onMutate} />
      <ResultsSection data={data} today={today} onMutate={onMutate} />
    </>
  );
}

function AppointmentRow({ appt, edd, onEdit, onBrief }) {
  const covers = appt.checkpointIds.length;
  return (
    <div className="listRow">
      <button className="rowMain" type="button" onClick={onEdit}>
        <span className="rowTop"><span className="lab">{appt.title || APPOINTMENT_KIND_LABELS[appt.kind]}</span><span className="gap" /><span className="val">{formatGestationalAge(gestationalAgeOn(edd, appt.date))}</span></span>
        <span className="rowMeta">{longDate(appt.date)}{appt.time ? `, ${appt.time}` : ''}{appt.location ? `, ${appt.location}` : ''}{covers ? `, covers ${covers}` : ''}{appt.status !== 'scheduled' ? `, ${appt.status}` : ''}</span>
      </button>
      {onBrief ? <div className="rowTools"><button className="btnTiny" type="button" onClick={onBrief}>Brief</button></div> : null}
    </div>
  );
}

function AppointmentForm({ draft, edd, views, onCancel, onSave, onDelete }) {
  const [form, setForm] = useState(draft);
  useEffect(() => setForm(draft), [draft]);
  const set = (patch) => setForm((current) => ({ ...current, ...patch }));
  const gaLabel = form.date ? formatGestationalAge(gestationalAgeOn(edd, form.date)) : '';
  const linkable = views.filter((view) => view.checkpoint.anchor === 'ga' || form.checkpointIds.includes(view.checkpoint.id));
  const selectedBundles = new Set(views.filter((view) => form.checkpointIds.includes(view.checkpoint.id) && view.checkpoint.bundle).map((view) => view.checkpoint.bundle));
  const missingSameDraw = views.filter((view) => selectedBundles.has(view.checkpoint.bundle) && !form.checkpointIds.includes(view.checkpoint.id) && !['done', 'declined', 'na'].includes(view.status));
  return (
    <form className="noteBlock apptForm" onSubmit={(event) => { event.preventDefault(); if (form.date) onSave(form); }}>
      <div className="noteHead"><span className="eyebrow">{form.id ? 'Edit appointment' : 'New appointment'}</span><span className="noteValue">{gaLabel}</span></div>
      <div className="formGrid flush">
        <div className="formPair">
          <label><span>Date</span><input className="field" type="date" value={form.date} onChange={(event) => set({ date: event.target.value })} required /></label>
          <label><span>Time</span><input className="field" type="time" value={form.time} onChange={(event) => set({ time: event.target.value })} /></label>
        </div>
        <div className="formPair">
          <label><span>Type</span><select className="field" value={form.kind} onChange={(event) => set({ kind: event.target.value })}>{Object.entries(APPOINTMENT_KIND_LABELS).map(([id, label]) => <option key={id} value={id}>{label}</option>)}</select></label>
          <label><span>Status</span><select className="field" value={form.status} onChange={(event) => set({ status: event.target.value })}><option value="scheduled">Scheduled</option><option value="done">Done</option><option value="cancelled">Cancelled</option></select></label>
        </div>
        <label><span>Title</span><input className="field" value={form.title} onChange={(event) => set({ title: event.target.value })} placeholder="20-week anatomy scan" /></label>
        <div className="formPair">
          <label><span>With</span><input className="field" value={form.provider} onChange={(event) => set({ provider: event.target.value })} /></label>
          <label><span>Where</span><input className="field" value={form.location} onChange={(event) => set({ location: event.target.value })} /></label>
        </div>
        <div className="coversBlock">
          <span className="coversLabel">This appointment covers</span>
          {missingSameDraw.length ? (
            <button className="hintAction" type="button" onClick={() => set({ checkpointIds: [...form.checkpointIds, ...missingSameDraw.map((view) => view.checkpoint.id)] })}>
              <Syringe size={14} />Add same-draw tests: {missingSameDraw.map((view) => view.checkpoint.title).join(', ')}
            </button>
          ) : null}
          <div className="chipWrap">
            {linkable.map((view) => {
              const on = form.checkpointIds.includes(view.checkpoint.id);
              const outside = form.date && (form.date < view.window.startDate || form.date > view.window.endDate);
              return (
                <button key={view.checkpoint.id} className={`chip${on ? ' on' : ''}${outside && !on ? ' dim' : ''}`} type="button" onClick={() => set({ checkpointIds: on ? form.checkpointIds.filter((id) => id !== view.checkpoint.id) : [...form.checkpointIds, view.checkpoint.id] })}>
                  {view.checkpoint.stick === 'venous' ? <Syringe size={12} className="inlineIcon" /> : null}{view.checkpoint.title}{on && outside ? ' ⚠' : ''}
                </button>
              );
            })}
          </div>
        </div>
        <label><span>Notes</span><textarea className="field" rows={3} value={form.notes} onChange={(event) => set({ notes: event.target.value })} /></label>
        <div className="actionRow flush">
          <button className="btn live" type="submit">Save</button>
          <button className="btn" type="button" onClick={onCancel}>Cancel</button>
          {onDelete ? <button className="btn" type="button" onClick={onDelete}><Trash2 size={15} />Delete</button> : null}
        </div>
      </div>
    </form>
  );
}

function QuestionsSection({ data, onMutate }) {
  const [text, setText] = useState('');
  const open = data.questions.filter((question) => !question.done);
  const answered = data.questions.filter((question) => question.done);
  const patch = (id, change) => onMutate((doc) => ({ ...doc, questions: doc.questions.map((question) => (question.id === id ? { ...question, ...change } : question)) }));
  return (
    <>
      <SectionHead label="Questions for the midwife" value={open.length ? `${open.length} open` : ''} live={open.length > 0} />
      <form className="inlineAdd" onSubmit={(event) => { event.preventDefault(); const value = text.trim(); if (!value) return; onMutate((doc) => ({ ...doc, questions: [...doc.questions, { id: newPregnancyId(), text: value, answer: '', done: false, createdAt: new Date().toISOString() }] })); setText(''); }}>
        <input className="field" value={text} onChange={(event) => setText(event.target.value)} placeholder="Add a question" aria-label="New question" />
        <button className="btn" type="submit" aria-label="Add question"><Plus size={16} /></button>
      </form>
      {open.length || answered.length ? (
        <div className="rowGroup">
          {[...open, ...answered].map((question) => (
            <div className="questionRow" key={question.id}>
              <button className={question.done ? 'checkbox on' : 'checkbox'} type="button" aria-label={question.done ? 'Mark open' : 'Mark answered'} onClick={() => patch(question.id, { done: !question.done })}>{question.done ? <Check size={16} strokeWidth={3.4} /> : null}</button>
              <div className="questionMain">
                <span className={question.done ? 'dailyName doneText' : 'dailyName'}>{question.text}</span>
                <CommitField value={question.answer} placeholder="Answer" onCommit={(answer) => patch(question.id, { answer })} />
              </div>
              <button className="iconBtn danger" type="button" aria-label="Delete question" onClick={() => onMutate((doc) => ({ ...doc, questions: doc.questions.filter((entry) => entry.id !== question.id) }))}><Trash2 size={16} /></button>
            </div>
          ))}
        </div>
      ) : null}
    </>
  );
}

function MeasurementsSection({ data, today, onMutate }) {
  const [form, setForm] = useState({ date: today, weight: '', bp: '', fundalCm: '', fetalHeartRate: '' });
  const unit = data.settings.weightUnit;
  const target = weightGainTarget(data);
  const base = Number(data.profile.prePregnancyWeight) || null;
  function submit(event) {
    event.preventDefault();
    const [systolic, diastolic] = form.bp.split('/').map((part) => Number(part.trim()) || null);
    onMutate((doc) => ({ ...doc, measurements: [...doc.measurements, { id: newPregnancyId(), date: form.date, weight: Number(form.weight) || null, systolic: systolic ?? null, diastolic: diastolic ?? null, fundalCm: Number(form.fundalCm) || null, fetalHeartRate: Number(form.fetalHeartRate) || null, note: '' }] }));
    setForm({ date: today, weight: '', bp: '', fundalCm: '', fetalHeartRate: '' });
  }
  return (
    <>
      <SectionHead label="Measurements" value={target ? `Gain goal ${target.low}–${target.high} ${unit}` : ''} />
      <form className="formGrid flushTop" onSubmit={submit}>
        <div className="formPair">
          <label><span>Date</span><input className="field" type="date" value={form.date} onChange={(event) => setForm({ ...form, date: event.target.value })} required /></label>
          <label><span>Weight ({unit})</span><input className="field" inputMode="decimal" value={form.weight} onChange={(event) => setForm({ ...form, weight: event.target.value })} /></label>
        </div>
        <div className="formPair three">
          <label><span>BP</span><input className="field" value={form.bp} onChange={(event) => setForm({ ...form, bp: event.target.value })} placeholder="118/76" /></label>
          <label><span>Fundal (cm)</span><input className="field" inputMode="decimal" value={form.fundalCm} onChange={(event) => setForm({ ...form, fundalCm: event.target.value })} /></label>
          <label><span>Baby HR</span><input className="field" inputMode="numeric" value={form.fetalHeartRate} onChange={(event) => setForm({ ...form, fetalHeartRate: event.target.value })} /></label>
        </div>
        <button className="btn" type="submit">Log measurement</button>
      </form>
      {data.measurements.length ? (
        <div className="rowGroup spacedTop">
          {[...data.measurements].reverse().map((entry) => (
            <div className="listRow" key={entry.id}>
              <div className="rowMain">
                <span className="rowTop"><span className="lab">{formatShortDate(entry.date)}</span><span className="gap" /><span className="val">{[entry.weight != null ? `${entry.weight} ${unit}${base ? ` (${entry.weight - base >= 0 ? '+' : ''}${Math.round((entry.weight - base) * 10) / 10})` : ''}` : null, entry.systolic && entry.diastolic ? `${entry.systolic}/${entry.diastolic}` : null].filter(Boolean).join(', ')}</span></span>
                {entry.fundalCm || entry.fetalHeartRate ? <span className="rowMeta">{[entry.fundalCm ? `Fundal ${entry.fundalCm} cm` : null, entry.fetalHeartRate ? `Baby HR ${entry.fetalHeartRate}` : null].filter(Boolean).join(', ')}</span> : null}
              </div>
              <button className="iconBtn danger" type="button" aria-label="Delete measurement" onClick={() => onMutate((doc) => ({ ...doc, measurements: doc.measurements.filter((item) => item.id !== entry.id) }))}><Trash2 size={16} /></button>
            </div>
          ))}
        </div>
      ) : null}
    </>
  );
}

function ResultsSection({ data, today, onMutate }) {
  const [form, setForm] = useState({ date: today, test: '', result: '' });
  return (
    <>
      <SectionHead label="Test results" value={data.results.length ? String(data.results.length) : ''} />
      <form className="formGrid flushTop" onSubmit={(event) => { event.preventDefault(); if (!form.test.trim()) return; onMutate((doc) => ({ ...doc, results: [...doc.results, { id: newPregnancyId(), date: form.date, test: form.test.trim(), result: form.result, note: '' }] })); setForm({ date: today, test: '', result: '' }); }}>
        <div className="formPair">
          <label><span>Date</span><input className="field" type="date" value={form.date} onChange={(event) => setForm({ ...form, date: event.target.value })} required /></label>
          <label><span>Test</span><input className="field" value={form.test} onChange={(event) => setForm({ ...form, test: event.target.value })} placeholder="1-hour glucose" /></label>
        </div>
        <label><span>Result</span><input className="field" value={form.result} onChange={(event) => setForm({ ...form, result: event.target.value })} placeholder="112 mg/dL, normal" /></label>
        <button className="btn" type="submit">Log result</button>
      </form>
      {data.results.length ? (
        <div className="rowGroup spacedTop">
          {[...data.results].reverse().map((entry) => (
            <div className="listRow" key={entry.id}>
              <div className="rowMain"><span className="rowTop"><span className="lab">{entry.test}</span><span className="gap" /><span className="val">{formatShortDate(entry.date)}</span></span><span className="rowMeta">{entry.result}</span></div>
              <button className="iconBtn danger" type="button" aria-label="Delete result" onClick={() => onMutate((doc) => ({ ...doc, results: doc.results.filter((item) => item.id !== entry.id) }))}><Trash2 size={16} /></button>
            </div>
          ))}
        </div>
      ) : null}
    </>
  );
}

// ---------- info ----------

function InfoTab({ data, onMutate }) {
  const dating = resolvePregnancyDating(data.dating);
  const target = weightGainTarget(data);
  const setDating = (patch) => onMutate((doc) => ({ ...doc, dating: { ...doc.dating, ...patch } }));
  const setProfile = (id, value) => onMutate((doc) => ({ ...doc, profile: { ...doc.profile, [id]: value } }));
  const numberOrNull = (value) => (value === '' ? null : Number(value));

  return (
    <>
      <SectionHead label="Dating" />
      <div className="noteBlock flushTop">
        <div className="noteHead"><span className="eyebrow">Due date</span><span className="noteValue">{dating ? longDate(dating.edd) : 'Not set'}</span></div>
        {dating ? <p className="noteBody">{DATING_METHOD_LABELS[dating.method]}. {dating.note}</p> : null}
        <div className="formGrid flush">
          <div className="formPair">
            <label><span>Last period started</span><input className="field" type="date" value={data.dating.lmp ?? ''} onChange={(event) => setDating({ lmp: event.target.value || null })} /></label>
            <label><span>Cycle (days)</span><CommitField inputMode="numeric" value={String(data.dating.cycleLengthDays)} onCommit={(value) => setDating({ cycleLengthDays: Number(value) || 28 })} /></label>
          </div>
          <div className="formPair three">
            <label><span>Ultrasound date</span><input className="field" type="date" value={data.dating.ultrasoundDate ?? ''} onChange={(event) => setDating({ ultrasoundDate: event.target.value || null })} /></label>
            <label><span>Measured weeks</span><CommitField inputMode="numeric" value={data.dating.ultrasoundGaWeeks ?? ''} onCommit={(value) => setDating({ ultrasoundGaWeeks: numberOrNull(value) })} /></label>
            <label><span>+ days</span><CommitField inputMode="numeric" value={data.dating.ultrasoundGaDays ?? ''} onCommit={(value) => setDating({ ultrasoundGaDays: numberOrNull(value) })} /></label>
          </div>
          <div className="formPair">
            <label><span>IVF transfer date</span><input className="field" type="date" value={data.dating.ivfTransferDate ?? ''} onChange={(event) => setDating({ ivfTransferDate: event.target.value || null })} /></label>
            <label><span>Embryo age</span><select className="field" value={data.dating.ivfEmbryoAgeDays ?? ''} onChange={(event) => setDating({ ivfEmbryoAgeDays: numberOrNull(event.target.value) })}><option value="">—</option><option value="3">Day 3</option><option value="5">Day 5</option></select></label>
          </div>
          <div className="formPair">
            <label><span>Due date given to us</span><input className="field" type="date" value={data.dating.manualEdd ?? ''} onChange={(event) => setDating({ manualEdd: event.target.value || null })} /></label>
            <label><span>Use</span><select className="field" value={data.dating.method} onChange={(event) => setDating({ method: event.target.value })}><option value="auto">Best available</option>{Object.entries(DATING_METHOD_LABELS).map(([id, label]) => <option key={id} value={id}>{label}</option>)}</select></label>
          </div>
          {dating && dating.discrepancyDays != null ? <p className="hint">Ultrasound vs last period: {dating.discrepancyDays} days apart (redate if more than {dating.redateThresholdDays}).</p> : null}
        </div>
      </div>

      <div className="formGrid">
        <div className="formPair">
          <label><span>Baby born on</span><input className="field" type="date" value={data.outcome.birthDate ?? ''} onChange={(event) => onMutate((doc) => ({ ...doc, outcome: { birthDate: event.target.value || null } }))} /></label>
          <label><span>Weight unit</span><select className="field" value={data.settings.weightUnit} onChange={(event) => onMutate((doc) => ({ ...doc, settings: { ...doc.settings, weightUnit: event.target.value } }))}><option value="lb">lb</option><option value="kg">kg</option></select></label>
        </div>
      </div>

      {PREGNANCY_PROFILE_GROUPS.map((group) => (
        <div key={group.id}>
          <SectionHead label={group.label} />
          <div className="formGrid flushTop">
            {PREGNANCY_PROFILE_FIELDS.filter((field) => field.group === group.id).map((field) => (
              <label key={field.id}>
                <span>{field.id === 'prePregnancyWeight' ? `${field.label} (${data.settings.weightUnit})` : field.label}</span>
                {field.type === 'select' ? (
                  <select className="field" value={data.profile[field.id] ?? ''} onChange={(event) => setProfile(field.id, event.target.value)}><option value="">—</option>{field.options.map((option) => <option key={option} value={option}>{option}</option>)}</select>
                ) : field.type === 'date' ? (
                  <input className="field" type="date" value={data.profile[field.id] ?? ''} onChange={(event) => setProfile(field.id, event.target.value)} />
                ) : (
                  <CommitField multiline={field.type === 'longtext'} inputMode={field.type === 'number' ? 'decimal' : undefined} value={data.profile[field.id] ?? ''} placeholder={field.placeholder} onCommit={(value) => setProfile(field.id, value)} />
                )}
              </label>
            ))}
            {group.id === 'health' && target ? <p className="hint">BMI {target.bmi} ({target.category}): typical total gain {target.low}–{target.high} {target.unit}.</p> : null}
          </div>
        </div>
      ))}
      <p className="hintBlock">{PREGNANCY_PLAN_DISCLAIMER}</p>
    </>
  );
}

// ---------- brief ----------

function BriefDialog({ data, date, onClose }) {
  const text = pregnancyVisitBrief(data, date);
  const [copied, setCopied] = useState(false);
  return (
    <div className="feedbackOverlay" role="presentation" onMouseDown={onClose}>
      <section className="feedbackDialog" role="dialog" aria-modal="true" aria-labelledby="brief-title" onMouseDown={(event) => event.stopPropagation()}>
        <div className="feedbackHead">
          <div><div className="eyebrow">{longDate(date)}</div><h1 className="pageTitle small" id="brief-title">Visit brief</h1></div>
          <button className="closeButton" type="button" onClick={onClose} aria-label="Close brief"><X size={16} strokeWidth={2.2} /></button>
        </div>
        <pre className="briefText">{text}</pre>
        <div className="actionRow">
          <button className="btn live" type="button" onClick={async () => { try { await navigator.clipboard.writeText(text); setCopied(true); } catch { setCopied(false); } }}><Copy size={15} />{copied ? 'Copied' : 'Copy'}</button>
        </div>
      </section>
    </div>
  );
}

